import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Expand, ImageIcon, PenLine, Play, Printer, Route, X } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import {
  resolveCheckoutFotoSrc,
  isCheckoutMediaVideo,
} from '../../checkout-view/utils/resolveCheckoutFotoSrc';
import { CheckoutViewOsRastreioModal } from '../../checkout-view/components/CheckoutViewOsRastreioModal';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import OsReportV2PreviewModal from '../../board/components/os-report-v2/OsReportV2PreviewModal';
import { buildRelatoriosOsReportOrder } from '../../relatorios/utils/relatoriosOsReportOrder';
import './OpenOsV2HistOsGoMedia.css';
import '../../relatorios/components/RelatoriosLaudoGoMedia.css';

const MODAL_Z = 100080;
const TIP_Z = 100070;

function signatureSrc(svg) {
  const s = String(svg || '').trim();
  if (!s) return '';
  if (s.startsWith('data:image/svg')) return s;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(s)}`;
}

export async function fetchOsGoAtendimento(numeroOS) {
  const token = localStorage.getItem('authToken');
  if (!token || !numeroOS) {
    return { fotos: [], assinaturaSvg: null, rastreio: { hasTrack: false } };
  }
  const base = getApiBaseUrl();
  const res = await fetch(
    `${base}/api/equipment/os-go-atendimento?numeroOS=${encodeURIComponent(numeroOS)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  if (!res.ok) {
    return { fotos: [], assinaturaSvg: null, rastreio: { hasTrack: false } };
  }
  const json = await res.json().catch(() => ({}));
  if (!json.success || !json.data) {
    return { fotos: [], assinaturaSvg: null, rastreio: { hasTrack: false } };
  }
  return {
    fotos: Array.isArray(json.data.fotos) ? json.data.fotos : [],
    assinaturaSvg: json.data.assinaturaSvg || null,
    assinaturaSignerName: json.data.assinaturaSignerName || null,
    rastreio: json.data.rastreio || { hasTrack: false },
    ciclos: Array.isArray(json.data.ciclos) ? json.data.ciclos : [],
  };
}

/**
 * Fotos/vídeos + assinatura + rastreio (App Go / LCMY_GO_*) abaixo do laudo no hist OS.
 */
export default function OpenOsV2HistOsGoMedia({ numeroOS, preset, embedded, printRow }) {
  const os = String(numeroOS || '').trim();
  const controlled = preset != null;
  const [loading, setLoading] = useState(!controlled);
  const [fotos, setFotos] = useState([]);
  const [assinaturaSvg, setAssinaturaSvg] = useState(null);
  const [rastreio, setRastreio] = useState({ hasTrack: false });
  const [viewerIdx, setViewerIdx] = useState(null);
  const [showAssinatura, setShowAssinatura] = useState(false);
  const [showRastreio, setShowRastreio] = useState(false);
  const [zoomFoto, setZoomFoto] = useState(null);
  const [reportOrder, setReportOrder] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);

  const viewFotos = controlled ? (Array.isArray(preset.fotos) ? preset.fotos : []) : fotos;
  const viewAssinatura = controlled ? (preset.assinaturaSvg || null) : assinaturaSvg;
  const viewSigner = controlled ? (preset.assinaturaSignerName || null) : null;
  const viewRastreio = controlled ? (preset.rastreio || { hasTrack: false }) : rastreio;
  const viewLoading = controlled ? false : loading;

  const openReport = useCallback(async () => {
    if (reportBusy || !printRow) return;
    setReportBusy(true);
    try {
      const order = await buildRelatoriosOsReportOrder(printRow, {
        assinaturaSvg: viewAssinatura,
        assinaturaSignerName: viewSigner,
        fotos: viewFotos
          .filter((f) => !isCheckoutMediaVideo(f))
          .map((f) => ({ src: resolveCheckoutFotoSrc(f), label: f.operacao || '' }))
          .filter((f) => f.src),
      });
      setReportOrder(order);
    } finally {
      setReportBusy(false);
    }
  }, [reportBusy, printRow, viewAssinatura, viewSigner, viewFotos]);

  useEffect(() => {
    if (controlled) return undefined;
    if (!os) {
      setLoading(false);
      setFotos([]);
      setAssinaturaSvg(null);
      setRastreio({ hasTrack: false });
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setViewerIdx(null);
    setShowAssinatura(false);
    setShowRastreio(false);
    setZoomFoto(null);
    fetchOsGoAtendimento(os).then((data) => {
      if (cancelled) return;
      setFotos(data.fotos || []);
      setAssinaturaSvg(data.assinaturaSvg || null);
      setRastreio(data.rastreio || { hasTrack: false });
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [os, controlled]);

  if (!os) return null;

  const hasFotos = viewFotos.length > 0;
  const hasAssinatura = !!viewAssinatura;
  const hasTrack = !!(viewRastreio && viewRastreio.hasTrack);
  const showSection = viewLoading || hasFotos || hasAssinatura || hasTrack;

  if (!showSection) return null;

  const activeFoto = viewerIdx != null ? viewFotos[viewerIdx] : null;
  const rastreioTip = viewRastreio.outsideRadius
    ? 'Atendimento iniciado fora do raio de atendimento'
    : 'Ver rastreio do atendimento';
  const rastreioBtn = hasTrack ? (
    <GoHoverTooltip text={rastreioTip} zIndex={TIP_Z} className="go-hover-tip-wrap--contents">
      <button
        type="button"
        className={`oosv2-hist-go-rastreio-btn${viewRastreio.outsideRadius ? ' is-outside' : ''}`}
        aria-label={rastreioTip}
        onClick={() => setShowRastreio(true)}
      >
        <Route size={13} strokeWidth={2.25} aria-hidden />
      </button>
    </GoHoverTooltip>
  ) : null;

  return (
    <>
      <div
        className={'oosv2-hist-go-media' + (embedded ? ' oosv2-hist-go-media--in-ciclo' : '')}
        onClick={(e) => e.stopPropagation()}
      >
        {embedded ? (
          <div className="oosv2-ciclo-go-head">
            {printRow ? (
              <GoHoverTooltip text="Impressão ou PDF do atendimento" zIndex={TIP_Z} className="go-hover-tip-wrap--contents">
                <button
                  type="button"
                  className="relatorios-laudo-go-print-btn"
                  onClick={openReport}
                  disabled={reportBusy}
                  aria-label="Impressão ou PDF do atendimento"
                >
                  <Printer size={13} strokeWidth={2.25} aria-hidden />
                </button>
              </GoHoverTooltip>
            ) : null}
            {rastreioBtn}
          </div>
        ) : (
          <>
            <div className="oosv2-hist-detail-sep" />
            <div className="oosv2-hist-go-media-label">Atendimento Go</div>
          </>
        )}
        {viewLoading ? (
          <div className="oosv2-hist-go-media-loading">Carregando mídia…</div>
        ) : (
          <div className="oosv2-hist-go-media-row">
            {hasFotos
              ? viewFotos.map((foto, idx) => {
                  const video = isCheckoutMediaVideo(foto);
                  const label = foto.operacao || (video ? `Vídeo ${idx + 1}` : `Foto ${idx + 1}`);
                return (
                  <GoHoverTooltip key={`f-${idx}`} text={label} zIndex={TIP_Z} className="go-hover-tip-wrap--contents">
                    <button
                      type="button"
                      className={`oosv2-hist-go-thumb${video ? ' is-video' : ''}`}
                      aria-label={label}
                      onClick={() => setViewerIdx(idx)}
                    >
                      {video ? (
                        <span className="oosv2-hist-go-thumb-play" aria-hidden>
                          <Play size={16} strokeWidth={2.2} />
                        </span>
                      ) : (
                        <img src={resolveCheckoutFotoSrc(foto)} alt="" />
                      )}
                    </button>
                  </GoHoverTooltip>
                );
                })
              : null}
            {hasAssinatura ? (
              <GoHoverTooltip text="Ver assinatura" zIndex={TIP_Z} className="go-hover-tip-wrap--contents">
              <button
                type="button"
                className="oosv2-hist-go-thumb oosv2-hist-go-thumb--sign"
                aria-label="Ver assinatura"
                onClick={() => setShowAssinatura(true)}
              >
                <img src={signatureSrc(viewAssinatura)} alt="" />
                <span className="oosv2-hist-go-thumb-badge">
                  <PenLine size={10} strokeWidth={2} aria-hidden />
                </span>
              </button>
              </GoHoverTooltip>
            ) : null}
            {embedded ? null : rastreioBtn}
            {!hasFotos && !hasAssinatura && !hasTrack ? (
              <span className="oosv2-hist-go-media-empty">Sem mídia Go nesta OS</span>
            ) : null}
          </div>
        )}
      </div>

      {viewerIdx != null && activeFoto
        ? createPortal(
            <div
              className="modal-overlay oosv2-hist-go-viewer-overlay"
              style={{ zIndex: MODAL_Z }}
              onClick={() => setViewerIdx(null)}
            >
              <div className="oosv2-hist-go-viewer" onClick={(e) => e.stopPropagation()}>
                <div className="oosv2-hist-go-viewer-header">
                  <div className="oosv2-hist-go-viewer-title">
                    <ImageIcon size={14} strokeWidth={2} aria-hidden />
                    <span>
                      {isCheckoutMediaVideo(activeFoto) ? 'Vídeo' : 'Foto'} · OS {os}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="modal-close-button"
                    onClick={() => setViewerIdx(null)}
                    aria-label="Fechar"
                  >
                    <X size={16} strokeWidth={2} />
                  </button>
                </div>
                <div className="oosv2-hist-go-viewer-body">
                  {viewFotos.length > 1 ? (
                    <div className="oosv2-hist-go-viewer-thumbs">
                      {viewFotos.map((foto, idx) => {
                        const video = isCheckoutMediaVideo(foto);
                        return (
                          <button
                            key={idx}
                            type="button"
                            className={`oosv2-hist-go-thumb${idx === viewerIdx ? ' is-active' : ''}${
                              video ? ' is-video' : ''
                            }`}
                            onClick={() => setViewerIdx(idx)}
                          >
                            {video ? (
                              <span className="oosv2-hist-go-thumb-play" aria-hidden>
                                <Play size={14} strokeWidth={2.2} />
                              </span>
                            ) : (
                              <img src={resolveCheckoutFotoSrc(foto)} alt="" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  <div className="oosv2-hist-go-viewer-main">
                    {isCheckoutMediaVideo(activeFoto) ? (
                      <video
                        key={resolveCheckoutFotoSrc(activeFoto)}
                        className="oosv2-hist-go-viewer-media"
                        src={resolveCheckoutFotoSrc(activeFoto)}
                        controls
                        playsInline
                        preload="metadata"
                        controlsList="nodownload"
                      />
                    ) : (
                      <div className="oosv2-hist-go-viewer-img-wrap">
                        <img
                          className="oosv2-hist-go-viewer-media"
                          src={resolveCheckoutFotoSrc(activeFoto)}
                          alt=""
                        />
                        <GoHoverTooltip text="Ampliar" zIndex={TIP_Z} className="go-hover-tip-wrap--contents">
                        <button
                          type="button"
                          className="oosv2-hist-go-zoom-btn"
                          aria-label="Ampliar"
                          onClick={() => setZoomFoto(activeFoto)}
                        >
                          <Expand size={14} strokeWidth={2} aria-hidden />
                        </button>
                        </GoHoverTooltip>
                      </div>
                    )}
                    {activeFoto.operacao ? (
                      <div className="oosv2-hist-go-viewer-caption">{activeFoto.operacao}</div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {showAssinatura && viewAssinatura
        ? createPortal(
            <div
              className="modal-overlay oosv2-hist-go-viewer-overlay"
              style={{ zIndex: MODAL_Z }}
              onClick={() => setShowAssinatura(false)}
            >
              <div
                className="oosv2-hist-go-viewer oosv2-hist-go-viewer--sign"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="oosv2-hist-go-viewer-header">
                  <div className="oosv2-hist-go-viewer-title">
                    <PenLine size={14} strokeWidth={2} aria-hidden />
                    <span>Assinatura · OS {os}</span>
                  </div>
                  <button
                    type="button"
                    className="modal-close-button"
                    onClick={() => setShowAssinatura(false)}
                    aria-label="Fechar"
                  >
                    <X size={16} strokeWidth={2} />
                  </button>
                </div>
                <div className="oosv2-hist-go-viewer-body oosv2-hist-go-sign-body">
                  <div className="oosv2-hist-go-sign-frame">
                    <img src={signatureSrc(viewAssinatura)} alt="" />
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {zoomFoto
        ? createPortal(
            <div
              className="modal-overlay oosv2-hist-go-viewer-overlay"
              style={{ zIndex: MODAL_Z + 1 }}
              onClick={() => setZoomFoto(null)}
            >
              <div className="oosv2-hist-go-zoom" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="modal-close-button"
                  onClick={() => setZoomFoto(null)}
                  aria-label="Fechar"
                >
                  <X size={16} strokeWidth={2} />
                </button>
                <img
                  src={resolveCheckoutFotoSrc(zoomFoto)}
                  alt=""
                  className="oosv2-hist-go-zoom-img"
                />
              </div>
            </div>,
            document.body
          )
        : null}

      {showRastreio && hasTrack ? (
        <CheckoutViewOsRastreioModal
          open={showRastreio}
          onClose={() => setShowRastreio(false)}
          os={{ numeroOS: os }}
          trackData={viewRastreio}
          zIndex={MODAL_Z}
        />
      ) : null}

      <OsReportV2PreviewModal
        open={!!reportOrder}
        order={reportOrder}
        includeHistory={false}
        onClose={() => setReportOrder(null)}
      />
    </>
  );
}
